import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Filter } from "lucide-react";
import { useLocation } from "react-router-dom";
import { IcCancel } from "@racoon/icons";
import { Namespace, useTypedT } from "@racoon/i18n";
import {
  DashboardMap,
  type EnrichedPickupLocation,
  type FlyToRequest,
} from "../components/map.dashboard";
import { MapSearch } from "../components/map-search.dashboard";
import { useWasteStatus } from "../lib/use-waste-status.hook";
import { useTrucksByProvider } from "../lib/use-trucks-by-provider.hook";
import { useMapTrucks } from "../lib/use-map-trucks.hook";
import { useDashboardFilters } from "../lib/use-dashboard-filters.hook";
import {
  BottomSheetDefault,
  BottomSheetTruck,
  BottomSheetPickupLocation,
  BottomSheetDisposalSite,
  BottomSheetDepot,
  BottomSheetSuspiciousLocation,
} from "../components/bottom-sheet.dashboard";
import {
  useGetGovGeoAccessQuery,
  useDisposalSiteLocationsQuery,
  useDepotsQuery,
  useSuspiciousLocationsQuery,
  useJobsQuery,
} from "~/shared/api/govDash/govDash.api";
import {
  FilterForm,
  type ExtraFilterItem,
  type FilterTabItem,
} from "../../../features/filter/filter.form";
import { ConnectProto } from "@racoon/api";
import { DatePicker } from "@racoon/ui";
import { startOfDay, endOfDay } from "date-fns";

type GovPickupLocationInfo = EnrichedPickupLocation;
type GovTruckInfo = ConnectProto.GovTruckInfo;
type Location = ConnectProto.Location;
type GovDepotInfo = ConnectProto.GovDepotInfo;

type SelectionState =
  | { type: "none" }
  | { type: "truck"; truckId: string | number }
  | { type: "pickup"; data: GovPickupLocationInfo }
  | { type: "disposalSite"; data: Location }
  | { type: "depot"; data: GovDepotInfo }
  | { type: "suspiciousLocation"; data: ConnectProto.SuspiciousLocation };

type GovGeoAccess = ConnectProto.GovGeoAccess;
// ── Dashboard Page ──
export const DashboardPage = () => {
  const t = useTypedT(Namespace.Govdash);
  const location = useLocation();

  const { data: govGeoAccessResp } = useGetGovGeoAccessQuery({});
  const govGeoAccesses = useMemo(() => {
    return (govGeoAccessResp?.govGeoAccesses ||
      (govGeoAccessResp as Record<string, unknown>)?.gov_geo_accesses ||
      []) as GovGeoAccess[];
  }, [govGeoAccessResp]);

  // Use the extracted hook for filter logic
  const {
    extraFilters,
    setExtraFilters,
    govOrganizationQueryArg,
    showTrucks,
    showMvoTrucks,
    showGovernmentTrucks,
    setShowTrucks,
  } = useDashboardFilters(govGeoAccesses as GovGeoAccess[]);

  // Convert to proper BigInt arrays for RPC calls
  const govOrganizationQueryArgBigInt = useMemo(() => {
    return {
      districtOrganizationIds: (govOrganizationQueryArg.districtOrganizationIds ?? []).map((id) =>
        BigInt(id),
      ),
      serviceProviderIds: (govOrganizationQueryArg.serviceProviderIds ?? []).map((id) =>
        BigInt(id),
      ),
    };
  }, [govOrganizationQueryArg]);
  // Single-day filter for the jobs shown on the map. Defaults to today.
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const jobsTimeFilter = useMemo(
    () => ({
      from: { seconds: BigInt(Math.floor(startOfDay(selectedDate).getTime() / 1000)) },
      to: { seconds: BigInt(Math.floor(endOfDay(selectedDate).getTime() / 1000)) },
    }),
    [selectedDate],
  );

  // Pickup-location markers are derived from the jobs response (grouped by
  // pickupLocation) rather than from usePickupLocationsQuery.
  const { data: { jobs = [] } = {} } = useJobsQuery({
    ...govOrganizationQueryArgBigInt,
    timeFilter: jobsTimeFilter,
  });

  const { trucks, governmentTruckIds } = useMapTrucks({
    ...govOrganizationQueryArgBigInt,
    showMvoTrucks,
    showGovernmentTrucks,
  });
  const { data: disposalSiteLocationsResp } = useDisposalSiteLocationsQuery({});
  const { data: depotsResp } = useDepotsQuery(govOrganizationQueryArgBigInt);
  const { data: suspiciousLocationsResp } = useSuspiciousLocationsQuery({});

  // The jobs response is large (tens of thousands of rows), many of them sharing
  // the same pickup location. Group by pickup-location id so the map renders one
  // marker per location instead of one per job. We keep the first job's
  // pickupLocation as the representative (it carries id/name/lat/lon, the fields
  // the map needs) and skip anything without usable coordinates.
  const pickupLocations = useMemo(() => {
    // One entry per pickup-location id. The representative object is the first
    // job's pickupLocation (carries id/name/lat/lon), enriched with `_jobs` —
    // every job that belongs to that location.
    const byId = new Map<string, GovPickupLocationInfo & { _jobs: any[]; _serviceProvider: any }>();
    for (const job of jobs) {
      const pl = (job as any)?.pickupLocation;
      if (!pl || typeof pl.lat !== "number" || typeof pl.lon !== "number") continue;
      const key = String(pl.id);
      const existing = byId.get(key);
      if (existing) {
        existing._jobs.push(job);
      } else {
        byId.set(key, {
          ...(pl as GovPickupLocationInfo),
          _jobs: [job],
          _serviceProvider: job.serviceProvider ?? {},
        });
      }
    }
    return Array.from(byId.values());
  }, [jobs]);
  const disposalSites = useMemo(
    () => disposalSiteLocationsResp?.locations || [],
    [disposalSiteLocationsResp],
  );
  const depots = useMemo(() => depotsResp?.depots || [], [depotsResp]);

  // Apply frontend filters for special-equipment
  const filteredPickupLocations = useMemo(() => {
    // COMMENTED: waste-generator filter logic
    // const wasteGeneratorFilter = extraFilters.find((f) => f.key === "waste-generator");
    // const selectedWasteGenerators = wasteGeneratorFilter?.selectedOptions || [];
    // if (selectedWasteGenerators.length === 0) {
    //   return pickupLocations;
    // }
    // return pickupLocations.filter((loc: GovPickupLocationInfo) => {
    //   const locType = loc.type;
    //   let typeId: string | null = null;
    //   if (locType === "PICKUP_LOCATION_TYPE_CONSTRUCTION" || locType === 1) typeId = "construction";
    //   else if (locType === "PICKUP_LOCATION_TYPE_HOUSEHOLD" || locType === 2) typeId = "household";
    //   return typeId && selectedWasteGenerators.includes(typeId);
    // });
    return pickupLocations;
  }, [pickupLocations]);

  const filteredTrucks = useMemo(() => {
    // COMMENTED: special-equipment filter logic
    // const equipmentFilter = extraFilters.find((f) => f.key === "special-equipment");
    // const selectedEquipment = equipmentFilter?.selectedOptions || [];
    // if (selectedEquipment.length === 0) {
    //   return trucks;
    // }
    // return trucks.filter((truck: GovTruckInfo) => {
    //   const containerTypes = truck.truck_type?.container_type_list || [];
    //   return containerTypes.some((ct: any) => {
    //     const ctName = ct.name?.toLowerCase() || "";
    //     return selectedEquipment.some((eq) => ctName.includes(eq));
    //   });
    // });
    return trucks;
  }, [trucks]);

  const actualWasteStatus = useWasteStatus({
    districtOrganizationIds: govOrganizationQueryArg.districtOrganizationIds,
    serviceProviderIds: govOrganizationQueryArg.serviceProviderIds,
    selectedDate,
  });

  const truckProviderGroups = useTrucksByProvider({
    districtOrganizationIds: govOrganizationQueryArg.districtOrganizationIds,
    serviceProviderIds: govOrganizationQueryArg.serviceProviderIds,
  });

  const [selection, setSelection] = useState<SelectionState>({ type: "none" });
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedTabKey, setSelectedTabKey] = useState<string>("all");

  // Reset the open bottom sheet whenever the filter parameters change: the
  // previously selected item may no longer be present in the new dataset.
  const filterParamsKey = useMemo(
    () =>
      JSON.stringify({
        districtOrganizationIds: govOrganizationQueryArg.districtOrganizationIds,
        serviceProviderIds: govOrganizationQueryArg.serviceProviderIds,
        selectedDate: selectedDate.toDateString(),
      }),
    [govOrganizationQueryArg, selectedDate],
  );
  const previousFilterParamsKey = useRef(filterParamsKey);
  useEffect(() => {
    if (previousFilterParamsKey.current === filterParamsKey) return;
    previousFilterParamsKey.current = filterParamsKey;
    setSelection({ type: "none" });
  }, [filterParamsKey]);

  // Restore state from navigation (e.g., returning from records page)
  useEffect(() => {
    const state = location.state as any;

    let shouldClearState = false;

    if (state?.showTrucks) {
      setShowTrucks(true);
      shouldClearState = true;
    }
    if (state?.mapState) {
      sessionStorage.setItem("dashboard_map_state", JSON.stringify(state.mapState));
      shouldClearState = true;
    }

    // Clear the state after using it to prevent it from being reapplied on refresh
    if (shouldClearState) {
      window.history.replaceState({}, document.title);
    }
  }, [setShowTrucks, location.key]); // Use location.key to trigger only on navigation, not on state changes

  const filterTabs: FilterTabItem[] = useMemo(() => {
    // Count active filters instead of locations
    const districtFilter = extraFilters.find((f) => f.key === "district");
    const mvoFilter = extraFilters.find((f) => f.key === "mvo");
    const trucksFilter = extraFilters.find((f) => f.key === "trucks");

    const activeFiltersCount =
      (districtFilter?.selectedOptions.length || 0) +
      (mvoFilter?.selectedOptions.length || 0) +
      (trucksFilter?.selectedOptions.length || 0);

    return [
      {
        key: "all",
        label: t("tabs.all"),
        count: activeFiltersCount,
      },
      {
        key: "so",
        label: t("tabs.so"),
        count: activeFiltersCount,
      },
      {
        key: "tbo",
        label: t("tabs.tbo"),
        count: activeFiltersCount,
      },
    ];
  }, [t, extraFilters]);

  const handleTruckClick = useCallback((truck: GovTruckInfo) => {
    const truckId = truck.truck?.id;
    setSelection({
      type: "truck",
      truckId:
        typeof truckId === "bigint" ? truckId.toString() : truckId != null ? String(truckId) : "",
    });
  }, []);

  const handlePickupClick = useCallback((loc: GovPickupLocationInfo) => {
    setSelection({ type: "pickup", data: loc });
  }, []);

  const handleDisposalSiteClick = useCallback((site: Location) => {
    setSelection({ type: "disposalSite", data: site });
  }, []);

  const handleDepotClick = useCallback((depot: GovDepotInfo) => {
    setSelection({ type: "depot", data: depot });
  }, []);

  const handleCloseBottomSheet = useCallback(() => {
    setSelection({ type: "none" });
  }, []);

  const handleSuspiciousLocationClick = useCallback((location: ConnectProto.SuspiciousLocation) => {
    setSelection({ type: "suspiciousLocation", data: location });
  }, []);

  // A fresh object per request, so selecting the same place twice flies again.
  const [flyTo, setFlyTo] = useState<FlyToRequest | null>(null);
  const handleFlyTo = useCallback((target: FlyToRequest) => {
    setFlyTo({ lat: target.lat, lon: target.lon });
  }, []);

  const handleFilterApply = useCallback(
    (payload: { selectedTabKey: string; newSelected: ExtraFilterItem[] }) => {
      setSelectedTabKey(payload.selectedTabKey);
      setExtraFilters(payload.newSelected);
      setFilterOpen(false);
    },
    [],
  );

  // Get first active filter for display
  const firstActiveFilterLabel = useMemo(() => {
    for (const filter of extraFilters) {
      if (filter.selectedOptions.length > 0) {
        const firstOption = filter.options.find((opt) => opt.value === filter.selectedOptions[0]);
        return firstOption?.label || null;
      }
    }
    return null;
  }, [extraFilters]);

  // Count of additional filters (excluding first)
  const additionalFiltersCount = useMemo(() => {
    let count = 0;
    let firstFound = false;

    for (const filter of extraFilters) {
      if (filter.selectedOptions.length > 0) {
        if (!firstFound) {
          // Skip first selected filter, but count its remaining options
          count += filter.selectedOptions.length - 1;
          firstFound = true;
        } else {
          count += filter.selectedOptions.length;
        }
      }
    }
    return count;
  }, [extraFilters]);

  return (
    <div className="w-full h-full flex lg:flex-row flex-col lg:gap-4">
      <div className="w-full h-full flex flex-col lg:gap-4">
        {/* Map Container extracted to its own component */}
        <DashboardMap
          pickupLocations={filteredPickupLocations}
          trucks={filteredTrucks as GovTruckInfo[]}
          governmentTruckIds={governmentTruckIds}
          disposalSites={disposalSites as Location[]}
          depots={depots as GovDepotInfo[]}
          selection={selection}
          onPickupClick={handlePickupClick}
          onTruckClick={handleTruckClick}
          onDisposalSiteClick={handleDisposalSiteClick}
          onDepotClick={handleDepotClick}
          onSuspiciousLocationClick={handleSuspiciousLocationClick}
          showTrucks={showTrucks}
          truckProviderGroups={truckProviderGroups}
          suspiciousLocations={suspiciousLocationsResp?.locations || []}
          flyTo={flyTo}
        >
          {/* Filter bar on top of the map */}
          <div className="absolute top-0 left-0 right-0 z-[400] flex items-center justify-end gap-2 flex-wrap p-2 pointer-events-none">
            <div className="pointer-events-auto shrink-0">
              <MapSearch
                pickupLocations={filteredPickupLocations}
                trucks={filteredTrucks as GovTruckInfo[]}
                disposalSites={disposalSites as Location[]}
                depots={depots as GovDepotInfo[]}
                suspiciousLocations={suspiciousLocationsResp?.locations || []}
                showTrucks={showTrucks}
                onPickupClick={handlePickupClick}
                onTruckClick={handleTruckClick}
                onDisposalSiteClick={handleDisposalSiteClick}
                onDepotClick={handleDepotClick}
                onSuspiciousLocationClick={handleSuspiciousLocationClick}
                onFlyTo={handleFlyTo}
              />
            </div>
            <div className="flex flex-1 flex-row-reverse flex-wrap items-center justify-start gap-2 pointer-events-auto">
              <div className="flex items-center gap-2 shrink-0">
                <DatePicker
                  value={selectedDate}
                  onChange={(date) => date && setSelectedDate(date)}
                  maxDate={new Date()}
                />
                <button
                  type="button"
                  aria-label={t("actions.openFilters")}
                  className="w-8 h-8 rounded-full bg-white flex items-center justify-center cursor-pointer p-0 hover:bg-slate-50 transition-colors"
                  onClick={() => setFilterOpen(true)}
                >
                  <Filter className="w-5 h-5 fill-brand-blue" strokeWidth={0} />
                </button>
              </div>
              <div className="flex min-w-0 items-center gap-2 flex-wrap justify-end">
                {firstActiveFilterLabel && (
                  <div className="bg-brand-blue rounded-2xl py-1 pl-3 pr-1 flex items-center gap-1 h-6 cursor-pointer">
                    <span className="font-medium text-sm leading-4 tracking-tight text-[#f2f4f7] whitespace-nowrap">
                      {firstActiveFilterLabel}
                    </span>
                    <button
                      type="button"
                      aria-label={t("actions.clearFirstFilter")}
                      className="w-4 h-4 text-white cursor-pointer bg-transparent border-none p-0 flex items-center justify-center"
                      onClick={() => {
                        setExtraFilters((prev) => {
                          let firstFilterRemoved = false;
                          return prev.map((filter) => {
                            if (!firstFilterRemoved && filter.selectedOptions.length > 0) {
                              firstFilterRemoved = true;
                              // Remove only the first option from the first active filter
                              return {
                                ...filter,
                                selectedOptions: filter.selectedOptions.slice(1),
                              };
                            }
                            return filter;
                          });
                        });
                      }}
                    >
                      <IcCancel className="w-full h-full" />
                    </button>
                  </div>
                )}
                {additionalFiltersCount > 0 && (
                  <div className="bg-brand-blue rounded-2xl py-1 pl-3 pr-1 flex items-center gap-1 h-6 cursor-pointer">
                    <span className="font-medium text-sm leading-4 tracking-tight text-[#f2f4f7] whitespace-nowrap">
                      +{additionalFiltersCount}
                    </span>
                    <button
                      type="button"
                      aria-label={t("actions.clearAdditionalFilters")}
                      className="w-4 h-4 text-white cursor-pointer bg-transparent border-none p-0 flex items-center justify-center"
                      onClick={() => {
                        setExtraFilters((prev) => {
                          let firstFilterFound = false;
                          return prev.map((filter) => {
                            if (filter.selectedOptions.length > 0) {
                              if (!firstFilterFound) {
                                firstFilterFound = true;
                                // Keep only the first option of the first filter
                                return {
                                  ...filter,
                                  selectedOptions: filter.selectedOptions.slice(0, 1),
                                };
                              } else {
                                // Remove all options from remaining filters
                                return { ...filter, selectedOptions: [] };
                              }
                            }
                            return filter;
                          });
                        });
                      }}
                    >
                      <IcCancel className="w-full h-full" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DashboardMap>

        {/* Bottom sheet - 3 states */}
        {!filterOpen && (
          <div className={selection.type === "none" ? "contents" : "hidden lg:contents"}>
            <BottomSheetDefault
              wasteStatus={actualWasteStatus}
              truckProviderGroups={truckProviderGroups}
              pickupLocations={filteredPickupLocations}
            />
          </div>
        )}

        {/* Filter Form — slides in from the right on desktop, fullscreen sheet on mobile */}
        <FilterForm
          open={filterOpen}
          onOpenChange={setFilterOpen}
          onApply={handleFilterApply}
          filterTabs={filterTabs}
          selectedTabKey={selectedTabKey}
          extraFilters={extraFilters}
        />
      </div>
      {!filterOpen && selection.type === "truck" && (
        <BottomSheetTruck
          truckId={selection.truckId}
          govGeoAccesses={govGeoAccesses}
          onClose={handleCloseBottomSheet}
        />
      )}
      {!filterOpen && selection.type === "pickup" && (
        <BottomSheetPickupLocation
          locationId={String(selection.data.id)}
          pickupLocation={selection.data}
          onClose={handleCloseBottomSheet}
        />
      )}
      {!filterOpen && selection.type === "disposalSite" && (
        <BottomSheetDisposalSite site={selection.data} onClose={handleCloseBottomSheet} />
      )}
      {!filterOpen && selection.type === "depot" && (
        <BottomSheetDepot depot={selection.data} onClose={handleCloseBottomSheet} />
      )}
      {!filterOpen && selection.type === "suspiciousLocation" && (
        <BottomSheetSuspiciousLocation
          suspiciousLocation={selection.data}
          onClose={handleCloseBottomSheet}
        />
      )}
    </div>
  );
};
